import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { H2, Kicker, Section, useLT } from "./sections";

export default function VatCalculator() {
  const t = useLT();
  const { lang } = useLang();
  const [mode, setMode] = useState("add");
  const [raw, setRaw] = useState("1000");
  const amount = Math.max(0, parseFloat(String(raw).replace(",", ".")) || 0);
  const net = mode === "add" ? amount : amount / 1.05;
  const vat = net * 0.05;
  const gross = net + vat;
  const fmt = (v) => v.toLocaleString({ ar: "ar-AE", fr: "fr-FR", ur: "ur-PK" }[lang] || "en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const rows = [["calcNet", net, "net"], ["calcVat", vat, "vat"], ["calcGross", gross, "gross"]];
  return (
    <Section id="calculator" className="grid gap-14 lg:grid-cols-2 lg:items-center">
      <div>
        <Kicker>{t("calcKicker")}</Kicker>
        <H2>{t("calcTitle")}</H2>
        <p className="mt-6 text-base leading-relaxed text-white/60">{t("calcText")}</p>
        <Link data-testid="calc-cta" to="/register" className="group mt-8 inline-flex items-center gap-2 text-sm font-bold text-[#00C462] hover:text-[#7EF0B4]">
          {t("calcCta")}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:rotate-180" />
        </Link>
      </div>
      <div data-testid="vat-calculator" className="rounded-2xl border border-white/10 bg-[#061A0F] p-6 shadow-2xl sm:p-8">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#D4AF37]/15 text-[#D4AF37]"><Calculator className="h-5 w-5" /></div>
          <div className="grid flex-1 grid-cols-2 rounded-full border border-white/10 bg-white/[0.03] p-1 text-xs font-bold">
            {[["add", "calcMode1"], ["remove", "calcMode2"]].map(([m, k]) => (
              <button key={m} type="button" data-testid={`calc-mode-${m}`} onClick={() => setMode(m)} className={`rounded-full px-3 py-2 transition-colors ${mode === m ? "bg-[#007A3D] text-white" : "text-white/60 hover:text-white"}`}>{t(k)}</button>
            ))}
          </div>
        </div>
        <label className="mt-6 block text-xs font-semibold uppercase tracking-wider text-white/40">{t("calcAmount")}</label>
        <div className="mt-2 flex items-center rounded-xl border border-white/15 bg-black/30 px-4 focus-within:border-[#00C462]">
          <span className="font-mono text-sm text-white/40">AED</span>
          <input data-testid="calc-amount-input" type="number" min="0" step="0.01" inputMode="decimal" value={raw} onChange={(e) => setRaw(e.target.value)} dir="ltr"
            className="h-14 w-full bg-transparent px-3 font-mono text-2xl font-bold tabular-nums text-white outline-none" />
        </div>
        <div className="mt-6 space-y-3">
          {rows.map(([k, v, id]) => (
            <div key={k} className={`flex items-center justify-between rounded-xl px-4 py-3 ${id === "gross" ? "border border-[#D4AF37]/30 bg-[#D4AF37]/5" : "bg-white/[0.03]"}`}>
              <span className="text-sm text-white/65">{t(k)}</span>
              <span data-testid={`calc-${id}`} className={`font-mono text-lg font-bold tabular-nums ${id === "gross" ? "text-[#D4AF37]" : id === "vat" ? "text-[#7EF0B4]" : "text-white"}`}>{fmt(v)} AED</span>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}
